import { useState, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, Save, X } from 'lucide-react';
import {
  listarConfiguracoes,
  salvarConfiguracao,
  PaymentInstallmentConfig,
} from '@/integrations/supabase/payment-installments';

export default function ConfiguracaoParcelamentos() {
  const { empresa } = useAuth();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<PaymentInstallmentConfig>>({});
  const [isSaving, setIsSaving] = useState(false);

  const { data: configuracoes = [], isLoading, error } = useQuery({
    queryKey: ['payment-installments', empresa?.id],
    queryFn: listarConfiguracoes,
    enabled: !!empresa?.id,
  });

  if (!empresa) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Nenhuma empresa selecionada</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-red-600">Erro ao carregar configurações: {String(error)}</p>
      </div>
    );
  }

  const handleEdit = (config: PaymentInstallmentConfig) => {
    setEditingId(config.id);
    setEditingData({
      max_installments: config.max_installments,
      days_between_installments: config.days_between_installments,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleSave = async (config: PaymentInstallmentConfig) => {
    if (!editingData.max_installments || !editingData.days_between_installments === undefined) {
      toast.error('Preencha todos os campos');
      return;
    }

    setIsSaving(true);
    try {
      const result = await salvarConfiguracao(
        config.payment_method,
        editingData.max_installments,
        editingData.days_between_installments as number
      );

      if (result.ok) {
        toast.success(`Configuração de "${config.payment_method}" atualizada com sucesso!`);
        setEditingId(null);
        setEditingData({});
        queryClient.invalidateQueries({ queryKey: ['payment-installments', empresa?.id] });
      } else {
        toast.error(result.error || 'Erro ao salvar configuração');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar configuração');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuração de Parcelamentos</h1>
        <p className="text-gray-600 mt-2">
          Defina quantas parcelas e o intervalo entre elas para cada forma de pagamento
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="space-y-2 text-sm">
            <p className="text-blue-900">
              <strong>💡 Como funciona:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>
                <strong>Max Parcelas:</strong> Número máximo de vezes que o cliente pode parcelar
              </li>
              <li>
                <strong>Dias entre Parcelas:</strong> Intervalo de dias entre cada parcela (0 = mesmo dia)
              </li>
              <li>Exemplo: Cartão com 3 parcelas e 30 dias → primeira (data A), segunda (data A + 30 dias), terceira (data A + 60 dias)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Configurations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Formas de Pagamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Forma de Pagamento</TableHead>
                  <TableHead className="text-center">Max Parcelas</TableHead>
                  <TableHead className="text-center">Dias entre Parcelas</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configuracoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Nenhuma configuração encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  configuracoes.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell className="font-medium">{config.payment_method}</TableCell>
                      <TableCell className="text-center">
                        {editingId === config.id ? (
                          <Input
                            type="number"
                            min="1"
                            value={editingData.max_installments || 1}
                            onChange={(e) =>
                              setEditingData({
                                ...editingData,
                                max_installments: parseInt(e.target.value) || 1,
                              })
                            }
                            className="w-20 h-9 text-center mx-auto"
                          />
                        ) : (
                          <span>{config.max_installments}x</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {editingId === config.id ? (
                          <Input
                            type="number"
                            min="0"
                            value={editingData.days_between_installments || 0}
                            onChange={(e) =>
                              setEditingData({
                                ...editingData,
                                days_between_installments: parseInt(e.target.value) || 0,
                              })
                            }
                            className="w-20 h-9 text-center mx-auto"
                          />
                        ) : (
                          <span>{config.days_between_installments} dias</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-2 justify-center">
                          {editingId === config.id ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancel()}
                                className="h-8 px-3"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSave(config)}
                                disabled={isSaving}
                                className="h-8 px-3"
                              >
                                {isSaving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(config)}
                              className="h-8 px-3"
                            >
                              Editar
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="space-y-2 text-sm">
            <p className="text-green-900">
              <strong>📌 Próximo Passo:</strong>
            </p>
            <p className="text-green-800">
              Quando criar um novo pedido, você poderá selecionar a forma de pagamento e depois escolher em quantas parcelas quer dividir. O sistema gerará automaticamente as datas de vencimento!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
